import React from 'react';
import cx from 'classnames';
import { StaticMap as Static } from '@vis.gl/react-google-maps';

import { triggerUserDraggedOnMapEvent } from 'Services/analytics/events';

import type { Locations } from '../../LocationSection.types';
import MapExpanderControl from '../Components/CustomMapControls/MapExpanderControl';
import MapZoomControl from '../Components/CustomMapControls/MapZoomControl';
import { INIT_ZOOM_LEVEL, MAX_ZOOM_LEVEL } from '../SwitchableMap.consts';
import { hasFullscreenSupport, isMaxZoomLevel } from '../SwitchableMap.helpers';
import type { InitialZoomLevel } from '../SwitchableMap.types';

import { useStaticMapUrl } from './StaticMap.helpers';

import styles from './StaticMap.module.scss';

export interface StaticMapProps {
	className?: string;
	distance: number;
	isDealerMarkerVisible?: boolean;
	locations: Locations;
	mapWidth: number;
	onClick: () => void;
	onMapError: (error: unknown) => void;
	setInitialZoomLevel: React.Dispatch<React.SetStateAction<InitialZoomLevel>>;
	setIsInitiallyExpanded: () => void;
}

export const StaticMap: React.FC<StaticMapProps> = ({
	className,
	distance,
	isDealerMarkerVisible = true,
	locations,
	mapWidth,
	onClick,
	onMapError,
	setInitialZoomLevel,
	setIsInitiallyExpanded,
}) => {
	const onError = () => {
		onMapError(new Error('Error loading static map'));
		onClick();
	};

	const staticMapUrl = useStaticMapUrl({
		distance,
		isDealerMarkerVisible,
		locations,
		mapWidth,
		onError,
	});

	const zoomLevel = isMaxZoomLevel(distance) ? MAX_ZOOM_LEVEL : INIT_ZOOM_LEVEL;

	return staticMapUrl ? (
		<div
			className={cx(styles.map, className)}
			data-testid="static-map"
			onClick={onClick}
			onDragEnd={() => triggerUserDraggedOnMapEvent('static_map')}
			onKeyDown={onClick}
			role="button"
			tabIndex={0}
		>
			<Static url={staticMapUrl} />
			<div className={styles.controls}>
				{hasFullscreenSupport() && (
					<div className={styles.expandedButton}>
						<MapExpanderControl isExpanded={false} onClick={setIsInitiallyExpanded} />
					</div>
				)}
				<div className={styles.zoomControls}>
					<MapZoomControl
						onZoomIn={() => setInitialZoomLevel('ZOOM_IN')}
						onZoomOut={() => setInitialZoomLevel('ZOOM_OUT')}
						zoomLevel={zoomLevel}
					/>
				</div>
			</div>
		</div>
	) : null;
};
